const hasWorker = registration => registration &&
    [registration.installing, registration.waiting, registration.active]
        .some(worker => worker && worker.state !== 'redundant')

// One monitor per page. Reacquire registrations rather than retaining the
// registration captured at startup: it may have been removed while we ran.
export function createServiceWorkerHealth({ serviceWorker, scriptURL, scope,
    online = () => true, visible = () => true, now = Date.now,
    schedule = setTimeout, cancel = clearTimeout,
    report = () => {}, onRegistration = () => {} }) {
    const interval = 5 * 60 * 1000
    let pending = null, timer = null, stopped = false, nextCheck = 0, failures = 0
    let current = null, detach = () => {}
    const log = (event, fields = {}) => {
        try { report(event, { ...fields, failures }) } catch (_) { /* Telemetry cannot break recovery. */ }
    }
    function bind(registration) {
        if (current === registration) return
        detach()
        current = registration
        detach = onRegistration(registration) || (() => {})
    }
    function arm() {
        cancel(timer)
        if (!stopped) timer = schedule(() => { void check('timer') }, Math.max(1000, nextCheck - now()))
    }
    async function inspect(reason) {
        let stage = 'lookup'
        try {
            let registration = await serviceWorker.getRegistration(scope)
            if (stopped) return
            // A root-scope lookup must not attach to a different app's worker.
            if (registration && registration.scope !== scope) registration = null
            let restored = false
            if (!hasWorker(registration)) {
                stage = 'register'
                registration = await serviceWorker.register(scriptURL, { scope })
                restored = true
            }
            if (stopped) return
            if (!hasWorker(registration)) throw new Error('Registration has no worker')
            bind(registration)
            // Registration already performs an update check. Avoid trying to
            // update again while a new installation is in progress.
            if (!restored && !registration.installing) {
                stage = 'update'
                try {
                    await registration.update()
                } catch (error) {
                    if (error?.name !== 'InvalidStateError') throw error
                    // The worker can vanish after the check above. One bounded
                    // re-registration handles this race without deleting caches.
                    stage = 'recover'
                    registration = await serviceWorker.register(scriptURL, { scope })
                    if (stopped) return
                    if (!hasWorker(registration)) throw new Error('Recovery returned no worker')
                    bind(registration)
                    restored = true
                }
            }
            if (stopped) return
            if (restored) log('registration_restored', { reason, stage })
            if (failures) log('checks_resumed', { reason })
            failures = 0
            nextCheck = now() + interval
        } catch (error) {
            if (stopped) return
            failures++
            const retryMs = Math.min(30 * 60 * 1000, 30000 * 2 ** Math.min(failures - 1, 6))
            nextCheck = now() + retryMs
            log('check_failed', { reason, stage, retry_ms: retryMs,
                error_name: error?.name || 'Error', error_message: String(error?.message || error).slice(0, 300) })
        }
    }
    function check(reason = 'manual') {
        if (stopped) return Promise.resolve()
        if (pending) return pending
        if (now() < nextCheck) return Promise.resolve()
        if (!online() || !visible()) {
            // Poll lightly while suspended; foreground/online events can check
            // immediately because they do not advance the failure backoff.
            cancel(timer)
            timer = schedule(() => { void check('timer') }, interval)
            return Promise.resolve()
        }
        pending = inspect(reason).finally(() => {
            pending = null
            arm()
        })
        return pending
    }
    return {
        check,
        stop() { stopped = true; cancel(timer); detach(); current = null }
    }
}
